/**
 * Goal-set validation.
 *
 * The invariant is on the *set*, not the individual goal: a manager can hold a
 * half-built draft, but the moment it goes to the employee for acceptance the
 * organizational goals must number at least 3 and total exactly 100.
 */

import { GOAL_TYPES, type GoalType } from './enums';

export const ORG_GOAL_TOTAL_WEIGHTAGE = 100;
export const MIN_ORG_GOALS = 3;

/** Individual goals are development goals - they never affect the official rating. */
export const INDIVIDUAL_GOAL_WEIGHTAGE = 0;

/**
 * An employee may hold at most two individual goals.
 *
 * The cap is on the *set*, like the weightage rule: it is not that a third goal is
 * invalid in itself, but that a development plan of fifteen certifications is not a
 * plan. Two is what the spec asks for and what a manager can actually discuss in a
 * 1-on-1.
 */
export const MAX_INDIVIDUAL_GOALS = 2;

export const MAX_GOAL_TITLE_LENGTH = 200;

export interface GoalDraft {
  title: string;
  type: GoalType;
  weightage: number;
}

export type GoalIssueCode =
  | 'EMPTY_TITLE'
  | 'TITLE_TOO_LONG'
  | 'UNKNOWN_TYPE'
  | 'WEIGHTAGE_NOT_INTEGER'
  | 'WEIGHTAGE_OUT_OF_RANGE'
  | 'INDIVIDUAL_MUST_BE_ZERO'
  | 'INDIVIDUAL_TOO_MANY'
  | 'ORG_TOO_FEW'
  | 'ORG_TOTAL_MISMATCH';

export interface GoalIssue {
  code: GoalIssueCode;
  message: string;
  /** Index into the submitted array, or null for whole-set issues. */
  index: number | null;
}

export interface GoalSetValidation {
  ok: boolean;
  issues: GoalIssue[];
  orgCount: number;
  orgTotalWeightage: number;
  individualCount: number;
  /** Signed distance from 100 - drives the "5% remaining" weightage meter. */
  remainingWeightage: number;
}

/**
 * Validate a full goal set for an employee's cycle.
 *
 * Weightage is required to be a whole number. That is not fussiness: three
 * goals at 33.33 total 99.99 and would fail an exact-100 check for reasons no
 * user could see or fix, so fractions are rejected at the door instead.
 */
export function validateGoalSet(goals: ReadonlyArray<GoalDraft>): GoalSetValidation {
  const issues: GoalIssue[] = [];

  goals.forEach((goal, index) => {
    const title = goal.title?.trim() ?? '';
    if (title.length === 0) {
      issues.push({ code: 'EMPTY_TITLE', message: 'Goal needs a title.', index });
    } else if (title.length > MAX_GOAL_TITLE_LENGTH) {
      issues.push({
        code: 'TITLE_TOO_LONG',
        message: `Goal title must be ${MAX_GOAL_TITLE_LENGTH} characters or fewer.`,
        index,
      });
    }

    if (!GOAL_TYPES.includes(goal.type)) {
      issues.push({ code: 'UNKNOWN_TYPE', message: `Unknown goal type "${goal.type}".`, index });
      return;
    }

    if (!Number.isInteger(goal.weightage)) {
      issues.push({
        code: 'WEIGHTAGE_NOT_INTEGER',
        message: 'Weightage must be a whole number.',
        index,
      });
      return;
    }

    if (goal.type === 'INDIVIDUAL') {
      if (goal.weightage !== INDIVIDUAL_GOAL_WEIGHTAGE) {
        issues.push({
          code: 'INDIVIDUAL_MUST_BE_ZERO',
          message: 'Individual goals carry 0% weightage and do not affect the rating.',
          index,
        });
      }
      return;
    }

    if (goal.weightage < 1 || goal.weightage > ORG_GOAL_TOTAL_WEIGHTAGE) {
      issues.push({
        code: 'WEIGHTAGE_OUT_OF_RANGE',
        message: `Weightage must be between 1 and ${ORG_GOAL_TOTAL_WEIGHTAGE}.`,
        index,
      });
    }
  });

  const org = goals.filter((g) => g.type === 'ORGANIZATIONAL');
  const individual = goals.filter((g) => g.type === 'INDIVIDUAL');
  const orgTotal = org.reduce((sum, g) => sum + (Number.isFinite(g.weightage) ? g.weightage : 0), 0);

  if (org.length < MIN_ORG_GOALS) {
    issues.push({
      code: 'ORG_TOO_FEW',
      message: `At least ${MIN_ORG_GOALS} organization goals are required (currently ${org.length}).`,
      index: null,
    });
  }

  if (individual.length > MAX_INDIVIDUAL_GOALS) {
    issues.push({
      code: 'INDIVIDUAL_TOO_MANY',
      message: `At most ${MAX_INDIVIDUAL_GOALS} individual goals are allowed (currently ${individual.length}).`,
      index: null,
    });
  }

  if (orgTotal !== ORG_GOAL_TOTAL_WEIGHTAGE) {
    const delta = ORG_GOAL_TOTAL_WEIGHTAGE - orgTotal;
    issues.push({
      code: 'ORG_TOTAL_MISMATCH',
      message:
        delta > 0
          ? `Total weightage is ${orgTotal}% - ${delta}% short of 100%.`
          : `Total weightage is ${orgTotal}% - ${Math.abs(delta)}% over 100%.`,
      index: null,
    });
  }

  return {
    ok: issues.length === 0,
    issues,
    orgCount: org.length,
    orgTotalWeightage: orgTotal,
    individualCount: individual.length,
    remainingWeightage: ORG_GOAL_TOTAL_WEIGHTAGE - orgTotal,
  };
}

export type KraSetIssueCode =
  | 'NO_KRAS'
  | 'DUPLICATE_KRA'
  | 'WEIGHTAGE_NOT_INTEGER'
  | 'WEIGHTAGE_OUT_OF_RANGE'
  | 'TOTAL_MISMATCH';

export interface KraSetIssue {
  code: KraSetIssueCode;
  message: string;
  /** Index into the submitted array, or null for whole-set issues. */
  index: number | null;
}

export interface KraSetValidation {
  ok: boolean;
  issues: KraSetIssue[];
  total: number;
  /** Signed distance from 100, driving the "Total weightage" readout. */
  remaining: number;
  /**
   * NOT an issue, deliberately.
   *
   * A set of two still totals 100 and is a legitimate thing to save, but the goal
   * set it produces would fail the at-least-3 rule the moment it was published.
   * Blocking the save would stop HR building a stencil they are still
   * filling in; saying nothing would let them find out at assignment time. So it is
   * surfaced as a warning and left to them.
   */
  tooFewToAccept: boolean;
}

/**
 * Validate a weighted KRA set - a reusable stencil like "Technical Architect" holding
 * ten KRAs that total exactly 100%.
 *
 * Exactly 100, not "at most": a set at 95% assigns a goal set that can never be
 * accepted, and the 5% would go missing silently rather than being noticed here.
 * Whole numbers only, for the same reason as `validateGoalSet` - 33.33 three times
 * totals 99.99 and fails an exact-100 check for reasons no user can see or fix.
 */
export function validateKraSet(
  items: ReadonlyArray<{ kraGroupId: string; weightage: number }>,
): KraSetValidation {
  const issues: KraSetIssue[] = [];
  const seen = new Set<string>();

  items.forEach((item, index) => {
    if (seen.has(item.kraGroupId)) {
      issues.push({
        code: 'DUPLICATE_KRA',
        message: 'That KRA is already in this set - change its weightage instead of adding it twice.',
        index,
      });
    }
    seen.add(item.kraGroupId);

    if (!Number.isInteger(item.weightage)) {
      issues.push({
        code: 'WEIGHTAGE_NOT_INTEGER',
        message: 'Weightage must be a whole number.',
        index,
      });
      return;
    }
    if (item.weightage < 1 || item.weightage > ORG_GOAL_TOTAL_WEIGHTAGE) {
      issues.push({
        code: 'WEIGHTAGE_OUT_OF_RANGE',
        message: `Weightage must be between 1 and ${ORG_GOAL_TOTAL_WEIGHTAGE}.`,
        index,
      });
    }
  });

  const total = items.reduce(
    (sum, item) => sum + (Number.isFinite(item.weightage) ? item.weightage : 0),
    0,
  );

  if (items.length === 0) {
    issues.push({ code: 'NO_KRAS', message: 'Add at least one KRA.', index: null });
  } else if (total !== ORG_GOAL_TOTAL_WEIGHTAGE) {
    const delta = ORG_GOAL_TOTAL_WEIGHTAGE - total;
    issues.push({
      code: 'TOTAL_MISMATCH',
      message:
        delta > 0
          ? `Total weightage is ${total}% — ${delta}% short of 100%.`
          : `Total weightage is ${total}% — ${Math.abs(delta)}% over 100%.`,
      index: null,
    });
  }

  return {
    ok: issues.length === 0,
    issues,
    total,
    remaining: ORG_GOAL_TOTAL_WEIGHTAGE - total,
    tooFewToAccept: items.length > 0 && items.length < MIN_ORG_GOALS,
  };
}

/** Whether another individual goal may be added. Same rule server- and client-side. */
export function canAddIndividualGoal(currentIndividualCount: number): boolean {
  return currentIndividualCount < MAX_INDIVIDUAL_GOALS;
}

/** Convenience for the UI's weightage meter, which runs on every keystroke. */
export function summariseWeightage(goals: ReadonlyArray<GoalDraft>): {
  total: number;
  remaining: number;
  complete: boolean;
} {
  const total = goals
    .filter((g) => g.type === 'ORGANIZATIONAL')
    .reduce((sum, g) => sum + (Number.isFinite(g.weightage) ? g.weightage : 0), 0);
  return {
    total,
    remaining: ORG_GOAL_TOTAL_WEIGHTAGE - total,
    complete: total === ORG_GOAL_TOTAL_WEIGHTAGE,
  };
}
